import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useAgent } from 'agents/react';
import { useAuth } from './AuthContext.jsx';
import { createLogger } from '../utils/logger.js';

const log = createLogger('WorkflowContext');

/**
 * WorkflowContext - Centralized workflow state with Cloudflare Agents SDK
 *
 * Uses official useAgent hook for real-time WebSocket communication.
 * The SDK handles connection, reconnection, and state sync automatically.
 *
 * KEY FEATURE: Persists workflow state to sessionStorage so WebSocket
 * reconnects automatically after browser refresh or navigation.
 */

const WorkflowContext = createContext(null);

/**
 * 🔧 Backend-Driven Architecture (No Client Storage)
 *
 * Instead of storing workflow state in localStorage/sessionStorage,
 * the frontend asks the backend on every app load:
 * "Do I have an active workflow?" via GET /api/v2/session/active
 *
 * The backend uses httpOnly cookies to identify the user and returns
 * active workflow info if one exists. This is more secure and reliable.
 */

// Step name to number mapping
// NOTE: Step 1 is "Preparing Analysis" in EnhancedAnalysisLoader (UI-only)
// Backend steps start at 2 to match the loader's step IDs
const STEP_MAP = {
  'research': 2,
  'validation': 3,
  'analysis': 4,
  'prompts': 5,
  'diagram': 6,   // Backend sends singular
  'diagrams': 6,  // Also handle plural for consistency
  'completed': 7,
};

// Initial workflow state
// 🔄 UNIFIED: Queue state is now part of workflow state
// Agent manages both queue and workflow status via single WebSocket
const initialWorkflowState = {
  workflowId: null,
  agentId: null,
  sessionId: null,
  isActive: false,
  // status: 'idle' | 'queued' | 'started' | 'in_progress' | 'completed' | 'failed' | 'cancelled'
  status: null,
  currentStep: 2, // Start at research step (step 1 is UI-only "Preparing")
  stepName: null,
  progress: null,
  loadingTitle: '',
  loadingMessage: '',
  batchProgress: null,
  data: {
    research: null,
    validation: null,
    analysis: null,
    prompts: null,
    flowDiagram: null,
  },
  wsConnected: false,
  error: null,
  startedAt: null,
  lastUpdatedAt: null,
  // 🎯 RATE LIMIT PROTECTION: Capacity waiting state
  isWaitingForCapacity: false,
  waitingStep: null,
  waitingMessage: null,
  waitingData: null,

  // 🔄 UNIFIED QUEUE STATE (merged from separate queueState)
  // When status === 'queued', these fields are populated
  queuePosition: null,
  queueEstimatedWait: null,
  queueMessage: null,

  // 🚀 DEPLOYMENT STATE (same agent, same WebSocket)
  // Populated when user deploys their app after 5-step analysis
  deployment: {
    isActive: false,
    deploymentId: null,
    status: null, // 'initialized' | 'generating' | 'building' | 'preview' | 'deploying' | 'completed' | 'failed'
    phase: null,  // Current phase: 'idle' | 'foundation' | 'core' | 'styling' | 'integration' | 'testing' | 'build'
    generationPhase: null,
    files: {},
    filesCount: 0,
    buildLogs: [],
    buildErrors: [],
    previewUrl: null,
    deployedUrl: null,
    githubUrl: null,
    chatHistory: [],
    error: null,
  },
};

// 🔄 UNIFIED ARCHITECTURE: Queue state is now part of workflowState
// No separate queueState needed - agent WebSocket handles all state sync

/**
 * Get the agent host based on environment
 * In development, use same-origin (via Vite proxy) for cookie support
 * In deployed environments, use VITE_BACKEND_URL (Worker domain)
 */
function getAgentHost() {
  if (import.meta.env.DEV) {
    // Use same-origin via Vite proxy (/agents → localhost:8787)
    // This ensures cookies are sent with WebSocket connections
    return 'localhost:3000';
  }
  // Deployed: Extract host from VITE_BACKEND_URL
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  if (backendUrl) {
    try {
      return new URL(backendUrl).host;
    } catch {
      // Fall through to error
    }
  }
  throw new Error('VITE_BACKEND_URL environment variable is required for builds');
}

/**
 * Agent Connection Component - Only renders when we have a valid agent ID
 * This prevents the useAgent hook from trying to connect to invalid endpoints
 */
function AgentConnection({ agentId, onMessage, onConnectionChange }) {
  const agent = useAgent({
    agent: 'architect-agent',
    name: agentId,
    host: getAgentHost(),
    onMessage,
    onOpen: () => {
      log.log('Agent WebSocket connected');
      onConnectionChange(true, agent);
    },
    onClose: () => {
      log.log('Agent WebSocket disconnected');
      onConnectionChange(false, null);
    },
    onError: (error) => {
      log.error('Agent WebSocket error:', error);
    },
  });

  // Store agent reference for parent access
  useEffect(() => {
    if (agent) {
      // Request initial status when connected
      try {
        agent.send(JSON.stringify({ type: 'get_project_status' }));
      } catch (e) {
        // Agent might not be ready yet
      }
    }
  }, [agent]);

  return null; // This component doesn't render anything
}

export function WorkflowProvider({ children }) {
  const [workflowState, setWorkflowState] = useState(initialWorkflowState);
  const [activePagePath, setActivePagePath] = useState(null);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const agentRef = useRef(null);

  // 🔧 FIX: Get auth state to prevent unnecessary API calls for guests
  const { authType, loading: authLoading, user } = useAuth();

  // 🔒 SECURITY: Clear workflow state when session changes
  // This prevents stale agentIds from trying to reconnect with old/different session
  const prevAuthType = useRef(authType);
  const prevSessionId = useRef(null);
  const hasInitializedSession = useRef(false);

  useEffect(() => {
    // Detect transition from authenticated → unauthenticated (logout)
    if (prevAuthType.current === 'authenticated' && authType === 'unauthenticated') {
      log.log('Auth state changed to unauthenticated - clearing workflow state');
      setCurrentAgentId(null);
      setWorkflowState(initialWorkflowState);
      hasInitializedSession.current = false;
    }

    // Detect session change (same user re-login via OAuth, or different user)
    const currentSessionId = user?.session?.id;

    if (currentSessionId) {
      // If we have an agentId from before the session was confirmed, clear it
      // This handles OAuth optimistic UI → session validation race condition
      if (!hasInitializedSession.current && currentAgentId) {
        log.log('Session confirmed after agentId was set - clearing stale workflow state', {
          sessionId: currentSessionId?.substring(0, 8) + '...',
          agentId: currentAgentId?.substring(0, 20) + '...'
        });
        setCurrentAgentId(null);
        setWorkflowState(initialWorkflowState);
      }

      // Normal session change detection
      if (prevSessionId.current && prevSessionId.current !== currentSessionId) {
        log.log('Session changed - clearing workflow state', {
          oldSession: prevSessionId.current?.substring(0, 8) + '...',
          newSession: currentSessionId?.substring(0, 8) + '...'
        });
        setCurrentAgentId(null);
        setWorkflowState(initialWorkflowState);
      }

      hasInitializedSession.current = true;
      prevSessionId.current = currentSessionId;
    }

    prevAuthType.current = authType;
  }, [authType, user?.session?.id, currentAgentId]);

  // 🔄 UNIFIED: No separate queue state - workflowState.status === 'queued' indicates queue mode

  /**
   * Handle messages from the Agent
   */
  const handleAgentMessage = useCallback((event) => {
    try {
      let message = typeof event.data === 'string'
        ? JSON.parse(event.data)
        : event.data;

      // 🔧 SDK FIX: Handle double-stringified messages from SDK
      // Sometimes SDK sends JSON string inside JSON string
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message);
        } catch (e) {
          // Not double-stringified, ignore
        }
      }

      // 🔧 SDK FIX: Cloudflare Agents SDK sends state sync without 'type' field at root level
      // Messages like {"state": {...}} need to be detected and handled as cf_agent_state
      // Also handle nested message structure from SDK
      let type = message?.type;
      if (!type && message?.state) {
        type = 'cf_agent_state';
      }
      // SDK sometimes wraps messages - check for nested data
      if (!type && message?.data?.type) {
        type = message.data.type;
      }
      // Also check for nested state in data wrapper (SDK internal sync)
      if (!type && message?.data?.state) {
        type = 'cf_agent_state';
      }
      log.log('Agent message:', type, (message?.state || message?.data?.state) ? '(has state)' : '');

      switch (type) {
        case 'agent_connected':
          log.log('Agent connected:', message.agentId);
          setWorkflowState(prev => ({ ...prev, wsConnected: true }));
          break;

        case 'step_progress':
        case 'step_started': {
          const stepName = message.step || message.stepName;
          const stepNum = STEP_MAP[stepName] || 1;
          setWorkflowState(prev => ({
            ...prev,
            status: 'in_progress',
            stepName,
            currentStep: stepNum,
            progress: {
              percentComplete: message.progress || 0,
              message: message.message || `Processing ${stepName}...`,
            },
            loadingTitle: stepName ? stepName.charAt(0).toUpperCase() + stepName.slice(1) : 'Processing',
            loadingMessage: message.message || '',
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'step_completed': {
          const stepName = message.step;
          const stepNum = STEP_MAP[stepName] || 1;
          if (message.data) {
            setWorkflowState(prev => ({
              ...prev,
              currentStep: stepNum,
              stepName,
              data: {
                ...prev.data,
                [stepName]: message.data,
                ...(stepName === 'diagram' || stepName === 'diagrams'
                  ? { flowDiagram: message.data }
                  : {}),
              },
              lastUpdatedAt: Date.now(),
            }));
          }
          break;
        }

        case 'batch_progress':
        case 'prompt_batch_completed': {
          setWorkflowState(prev => ({
            ...prev,
            batchProgress: {
              currentBatch: message.batchIndex ?? message.currentBatch,
              totalBatches: message.totalBatches,
              completedPrompts: message.totalCompleted ?? message.completedPrompts,
              targetPrompts: message.targetPrompts ?? message.totalTarget,
              percentComplete: message.progress ?? message.progressPercent,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        // 🎯 RATE LIMIT PROTECTION: Handle capacity waiting messages
        case 'step_waiting': {
          log.log('Step waiting for capacity:', message.step);
          setWorkflowState(prev => ({
            ...prev,
            isWaitingForCapacity: true,
            waitingStep: message.step,
            waitingMessage: message.message || 'Waiting for API capacity...',
            waitingData: {
              tokensRemaining: message.tokensRemaining,
              tokensLimit: message.tokensLimit,
              requestsRemaining: message.requestsRemaining,
              requestsLimit: message.requestsLimit,
              waitedSeconds: message.waitedSeconds,
              estimatedResetTime: message.estimatedResetTime,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'step_resuming': {
          log.log('Step resuming:', message.step);
          setWorkflowState(prev => ({
            ...prev,
            isWaitingForCapacity: false,
            waitingStep: null,
            waitingMessage: null,
            waitingData: null,
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'workflow_complete': {
          log.log('Workflow complete');
          const completedData = message.data || {};
          setWorkflowState(prev => ({
            ...prev,
            status: 'completed',
            isActive: false,
            currentStep: 7, // Completed step (step 7 in UI)
            stepName: 'completed',
            progress: { percentComplete: 100, message: 'Completed' },
            data: {
              // 🔧 FIX: Include idea, platform, and answers from backend broadcast
              idea: completedData.idea || prev.data.idea,
              platform: completedData.platform || prev.data.platform,
              answers: completedData.answers || prev.data.answers || [],
              research: completedData.research || prev.data.research,
              validation: completedData.validation || prev.data.validation,
              analysis: completedData.analysis || prev.data.analysis,
              prompts: completedData.prompts || prev.data.prompts,
              flowDiagram: completedData.diagram || completedData.diagrams || completedData.flowDiagram || prev.data.flowDiagram,
            },
            lastUpdatedAt: Date.now(),
          }));
          setCurrentAgentId(null); // Disconnect WebSocket
          break;
        }

        case 'workflow_error':
        case 'process_error': {
          log.error('Workflow error:', message.error);
          setWorkflowState(prev => ({
            ...prev,
            status: 'failed',
            isActive: false,
            error: message.error,
            lastUpdatedAt: Date.now(),
          }));
          setCurrentAgentId(null); // Disconnect WebSocket
          break;
        }

        case 'workflow_cancelled': {
          log.log('Workflow cancelled:', message.reason);
          setWorkflowState(prev => ({
            ...prev,
            status: 'cancelled',
            isActive: false,
            error: null,
            lastUpdatedAt: Date.now(),
          }));
          setCurrentAgentId(null); // Disconnect WebSocket
          break;
        }

        case 'workflow_resumed': {
          // Handle resume from cancelled/incomplete workflow
          log.log('Workflow resumed from step:', message.step);
          const stepName = message.step;
          const stepNum = STEP_MAP[stepName] || 2;
          const resumeData = message.data || {};
          setWorkflowState(prev => ({
            ...prev,
            status: 'in_progress',
            isActive: true,
            stepName,
            currentStep: stepNum,
            progress: {
              percentComplete: 0,
              message: message.message || `Resuming from ${stepName}...`,
            },
            loadingTitle: stepName ? stepName.charAt(0).toUpperCase() + stepName.slice(1) : 'Resuming',
            loadingMessage: message.message || `Resuming workflow from ${stepName}...`,
            lastUpdatedAt: Date.now(),
            // Clear any previous error state
            error: null,
            // Restore context and completed step data from Agent
            data: {
              ...prev.data,
              idea: message.idea || prev.data.idea,
              platform: message.platform || prev.data.platform,
              answers: message.answers || prev.data.answers || [],
              research: resumeData.research || prev.data.research,
              validation: resumeData.validation || prev.data.validation,
              analysis: resumeData.analysis || prev.data.analysis,
              prompts: resumeData.prompts || prev.data.prompts,
              flowDiagram: resumeData.flowDiagram || prev.data.flowDiagram,
            },
          }));
          break;
        }

        case 'project_status': {
          if (message.currentProgress) {
            const stepName = message.currentProgress.step;
            setWorkflowState(prev => ({
              ...prev,
              status: message.status || 'in_progress',
              stepName,
              currentStep: STEP_MAP[stepName] || 1,
              progress: {
                percentComplete: message.currentProgress.percentage,
                message: message.currentProgress.message,
              },
              lastUpdatedAt: Date.now(),
            }));
          }

          // 🚀 Handle deployment state from project_status
          if (message.deployment?.active) {
            log.log('Active deployment detected:', message.deployment.status, message.deployment.phase, 'mode:', message.deployment.mode);
            setWorkflowState(prev => ({
              ...prev,
              deployment: {
                ...prev.deployment,
                ...message.deployment,
                // Preserve files if we have them locally
                files: prev.deployment?.files || {},
                // 🔧 Preserve mode - don't let undefined overwrite existing mode
                mode: message.deployment.mode || prev.deployment?.mode,
              },
              isDeploymentActive: true,
              lastUpdatedAt: Date.now(),
            }));
          }
          break;
        }

        // 🔄 UNIFIED QUEUE: Handle agent state sync from Cloudflare Agents SDK
        // When agent calls setState(), SDK broadcasts to all connected WebSocket clients
        // SDK sends 'cf_agent_state' type for automatic state sync
        case 'cf_agent_state':
        case 'cf_agent_state_update':
        case 'state':
        case 'state_sync': {
          const agentState = message.state || message;
          log.log('Agent state sync:', agentState.status, agentState.progress?.step, 'deployment:', agentState.deployment?.status);

          // Map agent state to workflow state
          setWorkflowState(prev => {
            // 🔧 FIX: Sync deployment state from agent (includes chatHistory, previewUrl, etc.)
            let newDeployment = prev.deployment;
            if (agentState.deployment) {
              newDeployment = {
                ...prev.deployment,
                ...agentState.deployment,
                // Preserve local-only fields (not sent from agent)
                isActive: agentState.deployment.isActive ?? prev.deployment?.isActive,
                mode: prev.deployment?.mode || agentState.deployment?.mode,  // 🔧 Preserve 'vibe' or 'deploy'
              };
              log.log('Synced deployment state:', newDeployment.status, 'mode:', newDeployment.mode, 'chatHistory:', newDeployment.chatHistory?.length);
            }

            // Handle queue state
            if (agentState.status === 'queued') {
              return {
                ...prev,
                deployment: newDeployment,
                status: 'queued',
                isActive: true,
                queuePosition: agentState.queuePosition,
                queueEstimatedWait: agentState.queueEstimatedWait,
                queueMessage: agentState.queueMessage,
                lastUpdatedAt: Date.now(),
              };
            }

            // Handle transition from queued/cancelled to started (or any running state)
            if (['started', 'in_progress', 'resuming'].includes(agentState.status) && ['queued', 'cancelled'].includes(prev.status)) {
              log.log('Queue -> Started transition');
              const stepName = agentState.progress?.step || agentState.currentStep;
              return {
                ...prev,
                deployment: newDeployment,
                status: agentState.status,
                queuePosition: null,
                queueEstimatedWait: null,
                queueMessage: null,
                // Sync progress from agent
                stepName,
                currentStep: stepName ? STEP_MAP[stepName] || 1 : prev.currentStep,
                progress: agentState.progress ? {
                  percentComplete: agentState.progress.percentage,
                  message: agentState.progress.message,
                } : prev.progress,
                lastUpdatedAt: Date.now(),
              };
            }

            // Handle ongoing progress updates (agent is running)
            if (['started', 'in_progress', 'resuming'].includes(agentState.status) && agentState.progress) {
              const stepName = agentState.progress.step || agentState.currentStep;
              return {
                ...prev,
                deployment: newDeployment,
                status: agentState.status,
                stepName,
                currentStep: stepName ? STEP_MAP[stepName] || prev.currentStep : prev.currentStep,
                progress: {
                  percentComplete: agentState.progress.percentage,
                  message: agentState.progress.message,
                },
                lastUpdatedAt: Date.now(),
              };
            }

            // Handle other status updates (completed, failed, etc.)
            if (agentState.status && agentState.status !== prev.status) {
              return {
                ...prev,
                deployment: newDeployment,
                status: agentState.status,
                currentStep: agentState.currentStep ? STEP_MAP[agentState.currentStep] || prev.currentStep : prev.currentStep,
                lastUpdatedAt: Date.now(),
              };
            }

            // 🔧 FIX: Even if no status change, sync deployment state
            if (newDeployment !== prev.deployment) {
              return {
                ...prev,
                deployment: newDeployment,
                lastUpdatedAt: Date.now(),
              };
            }

            return prev;
          });
          break;
        }

        // ═══════════════════════════════════════════════════════════════════════
        // 🚀 DEPLOYMENT MESSAGE HANDLERS
        // Same agent, same WebSocket - deployment messages handled here
        // ═══════════════════════════════════════════════════════════════════════

        case 'deploy_started': {
          log.log('Deployment started:', message);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              isActive: true,
              mode: 'deploy',  // 🔧 Distinguish deploy from vibe
              status: 'generating',
              phase: message.phase || 'foundation',
              generationPhase: message.phase || 'foundation',
              error: null,
            },
            isDeploymentActive: true,
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_phase_start':
        case 'deploy_generation_phase': {
          log.log('Deploy phase:', message.phase);
          const phaseLabels = {
            foundation: '🏗️ Foundation',
            core: '⚙️ Core',
            styling: '🎨 Styling',
            integration: '🔌 Integration',
            testing: '🧪 Testing'
          };
          const phaseLabel = phaseLabels[message.phase] || message.phase;
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              phase: message.phase,
              generationPhase: message.phase,
              // Reset file tracking for new phase
              currentFileList: [],
              currentFileIndex: 0,
              currentFileName: null,
              // Add to activity log
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `Starting ${phaseLabel} phase...`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        // 🌊 NEW: File manifest - shows list of files to generate
        case 'deploy_phase_manifest': {
          log.log('Phase manifest:', message.phase, message.count, 'files');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              currentFileList: message.files || [],
              totalFilesInPhase: message.count || 0,
              currentFileIndex: 0,
              // Add to activity log
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `📋 ${message.count} files to generate`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        // 🌊 NEW: File started - highlights current file being generated
        case 'deploy_file_started': {
          log.log('Generating file:', message.path, `(${message.index}/${message.total})`);
          const fileName = message.path.split('/').pop();
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              currentFileName: message.path,
              currentFileIndex: message.index || 0,
              totalFilesInPhase: message.total || prev.deployment.totalFilesInPhase,
              // Add to activity log (every file for visibility)
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `📝 Generating ${fileName} (${message.index}/${message.total})`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        // 🌊 NEW: File chunk - real-time streaming (optional, for code preview)
        case 'deploy_file_chunk': {
          // Lightweight update - just track progress, don't store full content
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              currentFileProgress: message.totalLength || 0,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_file_generated':
        case 'deploy_file_created': {
          const file = message.file || message;
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              files: {
                ...prev.deployment.files,
                [file.path]: file.content || file.preview || true,
              },
              filesCount: Object.keys(prev.deployment.files).length + 1,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_build_started': {
          log.log('Build started');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'building',
              phase: 'build',
              buildLogs: [],  // Clear previous logs
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_build_log': {
          // Real-time streaming of build logs from sandbox
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              buildLogs: [...(prev.deployment.buildLogs || []), {
                message: message.message || message.log || message.data,
                type: message.logType || message.type,
                timestamp: message.timestamp
              }],
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_build_error': {
          log.error('Build error:', message.error);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              buildErrors: [...(prev.deployment.buildErrors || []), message.error],
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_files_written': {
          log.log('Deploy files written:', message.count);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              filesWritten: message.count,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_fixes_applied': {
          log.log('Deploy fixes applied:', message.files);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `🔧 Applied fixes to: ${(message.files || []).join(', ')}`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_fix_attempt': {
          log.log('Deploy fix attempt:', message.attempt, '/', message.maxAttempts);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              fixAttempt: message.attempt,
              maxFixAttempts: message.maxAttempts,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_visibility_changed': {
          log.log('Deploy visibility changed:', message.isPublic ? 'public' : 'private');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              previewUrl: message.previewUrl,
              isPublic: message.isPublic,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_preview_ready': {
          log.log('Preview ready:', message.url || message.previewUrl, 'isPublic:', message.isPublic);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'preview',
              phase: 'preview',
              previewUrl: message.url || message.previewUrl,
              isPublic: message.isPublic || false,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_build_completed': {
          log.log('Deploy build completed:', message.previewUrl);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'preview',
              phase: 'complete',
              previewUrl: message.previewUrl,
              isPublic: message.isPublic || false,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_chat_message': {
          const chatMsg = {
            role: message.role,
            content: message.content || message.message,
            timestamp: message.timestamp || new Date().toISOString(),
          };
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              chatHistory: [...prev.deployment.chatHistory, chatMsg],
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_deploying': {
          log.log('Deploying to production');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'deploying',
              phase: 'deploying',
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_complete': {
          log.log('Deployment complete:', message);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              isActive: false,
              status: 'completed',
              phase: 'completed',
              deployedUrl: message.url || message.deployedUrl,
              githubUrl: message.repo || message.githubUrl,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_error': {
          log.error('Deployment error:', message.error);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              isActive: false,
              status: 'failed',
              error: message.error,
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_cancelled': {
          log.log('Deployment cancelled');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              isActive: false,
              status: 'cancelled',
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_paused': {
          log.log('Deployment paused at phase:', message.phase, 'with', message.filesCount, 'files');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'paused',
              pausedAt: message.timestamp,
              pausedPhase: message.phase,
              // Add to activity log
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `Deployment paused at ${message.phase} phase (${message.filesCount} files generated)`,
                timestamp: message.timestamp
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'deploy_cleared': {
          log.log('Deployment cleared');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              isActive: false,
              deploymentId: null,
              status: 'idle',
              phase: null,
              generationPhase: null,
              files: {},
              filesCount: 0,
              buildLogs: [],
              buildErrors: [],
              previewUrl: null,
              deployedUrl: null,
              githubUrl: null,
              chatHistory: [],
              error: null,
              pausedAt: null,
              pausedPhase: null
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        // ═══════════════════════════════════════════════════════════════════════
        // 🎨 VIBE CODING MESSAGE HANDLERS
        // Same WebSocket, reuses deployment state for files/preview/build
        // ═══════════════════════════════════════════════════════════════════════

        case 'vibe_started': {
          log.log('Vibe coding started, previewUrl:', message.previewUrl, 'githubUrl:', message.githubUrl);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              isActive: true,
              mode: 'vibe',  // 🔧 Distinguish vibe from deploy
              status: 'generating',
              phase: 'vibe',
              generationPhase: 'vibe',
              deploymentId: message.vibeId || prev.deployment.deploymentId,
              // Restore saved URLs on session resume
              previewUrl: message.previewUrl || prev.deployment.previewUrl,
              githubUrl: message.githubUrl || prev.deployment.githubUrl,
              isPublic: message.isPublic || prev.deployment.isPublic || false,
              error: null,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: '🎨 Vibe coding session started. Building your app...',
                timestamp: new Date().toISOString()
              }]
            },
            isDeploymentActive: true,
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_generating': {
          log.log('Vibe generating code');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'generating',
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: '⚡ Generating code...',
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_file_generated': {
          // Backend sends: { type, file: 'path/to/file.js', content: '...', phase: '...' }
          const filePath = message.file || message.path;
          const fileContent = message.content;
          log.log('Vibe file generated:', filePath);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              files: {
                ...prev.deployment.files,
                [filePath]: fileContent || true,
              },
              filesCount: Object.keys(prev.deployment.files || {}).length + 1,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `📄 Generated: ${filePath}`,
                file: filePath,
                timestamp: message.timestamp || new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_chat_message': {
          const chatMsg = {
            role: message.role || 'assistant',
            content: message.content || message.message,
            timestamp: message.timestamp || new Date().toISOString(),
          };
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              chatHistory: [...(prev.deployment.chatHistory || []), chatMsg],
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_build_started': {
          log.log('Vibe build started');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'building',
              phase: 'build',
              buildLogs: [],  // Clear previous logs
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: '🔨 Building your app...',
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_build_log': {
          // Real-time streaming of build logs from sandbox
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              buildLogs: [...(prev.deployment.buildLogs || []), {
                message: message.message || message.data,
                type: message.logType || message.type,  // stdout, stderr, gate_start, gate_pass, gate_fail
                timestamp: message.timestamp
              }],
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_files_written': {
          log.log('Vibe files written:', message.count);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              filesWritten: message.count,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `📁 ${message.count} files written to sandbox`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_fixes_applied': {
          log.log('Vibe fixes applied:', message.files);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `🔧 Applied fixes to: ${(message.files || []).join(', ')}`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_fix_attempt': {
          log.log('Vibe fix attempt:', message.attempt, '/', message.maxAttempts);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              fixAttempt: message.attempt,
              maxFixAttempts: message.maxAttempts,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `🔧 Fix attempt ${message.attempt}/${message.maxAttempts} for ${message.failedGate || 'build'} errors...`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_visibility_changed': {
          log.log('Vibe visibility changed:', message.isPublic ? 'public' : 'private');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              previewUrl: message.previewUrl,
              isPublic: message.isPublic,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: message.isPublic
                  ? '🌐 Preview is now public - anyone with the link can view'
                  : '🔒 Preview is now private - only you can view',
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_preview_ready': {
          log.log('Vibe preview ready:', message.url || message.previewUrl, 'isPublic:', message.isPublic);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'preview',
              phase: 'preview',
              // generationPhase stays 'vibe' (not overwritten)
              previewUrl: message.url || message.previewUrl,
              isPublic: message.isPublic || false,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: message.isPublic
                  ? '✨ Preview ready! Your app is live (public).'
                  : '✨ Preview ready! Your app is live (private).',
                timestamp: new Date().toISOString()
              }]
            },
            isDeploymentActive: true,  // 🔧 Keep active so indicator shows
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_error': {
          log.error('Vibe error:', message.error);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              error: message.error,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `❌ Error: ${message.error}`,
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        // ═══════════════════════════════════════════════════════════════════════
        // 🎨 VIBE SESSION & GITHUB HANDLERS
        // ═══════════════════════════════════════════════════════════════════════

        case 'vibe_session_loaded': {
          log.log('Vibe session loaded:', message.source, message.filesCount, 'files, previewUrl:', message.previewUrl);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              mode: 'vibe',  // 🔧 Ensure vibe mode on session load
              deploymentId: message.deploymentId,
              filesCount: message.filesCount,
              source: message.source,  // 'state' | 'd1' | 'github' | 'template'
              // Restore saved URLs on session resume
              previewUrl: message.previewUrl || prev.deployment.previewUrl,
              githubUrl: message.githubUrl || prev.deployment.githubUrl,
              isPublic: message.isPublic || prev.deployment.isPublic || false,
            },
            isDeploymentActive: true,
          }));
          if (message.source !== 'template') {
            window.showToast?.(`Restored ${message.filesCount} files from ${message.source}`, 'success');
          }
          break;
        }

        case 'vibe_github_push_started': {
          log.log('GitHub push started');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              pushingToGitHub: true
            }
          }));
          break;
        }

        case 'vibe_github_push_progress': {
          log.log('GitHub push progress:', message.message);
          break;
        }

        case 'vibe_github_push_completed': {
          log.log('GitHub push completed:', message.githubUrl);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              githubUrl: message.githubUrl,
              pushingToGitHub: false
            }
          }));
          window.showToast?.('Pushed to GitHub successfully!', 'success');
          break;
        }

        case 'vibe_github_push_error': {
          log.error('GitHub push error:', message.error);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              pushingToGitHub: false
            }
          }));
          window.showToast?.(message.error, 'error');
          break;
        }

        // ═══════════════════════════════════════════════════════════════════════
        // 🎨 VIBE 5-PHASE GENERATION HANDLERS
        // ═══════════════════════════════════════════════════════════════════════

        case 'vibe_phase_started': {
          log.log('Vibe phase started:', message.phase);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'generating',
              phase: message.phase,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `📝 Phase: ${message.phase}`,
                timestamp: message.timestamp
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_phase_completed': {
          log.log('Vibe phase completed:', message.phase, 'files:', message.filesCount);
          break;
        }

        case 'vibe_phase_manifest': {
          log.log('Vibe phase manifest:', message.phase, 'files:', message.count);
          break;
        }

        case 'vibe_file_started': {
          log.log('Vibe file started:', message.path, `(${message.index}/${message.total})`);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              currentFile: message.path,
              fileProgress: { current: message.index, total: message.total }
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_file_chunk': {
          // Streaming chunk - no state update needed
          break;
        }

        case 'vibe_build_completed': {
          log.log('Vibe build completed, preview:', message.previewUrl);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'preview',
              previewUrl: message.previewUrl,
              previewVersion: (prev.deployment.previewVersion || 0) + 1, // Force iframe refresh
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_stopped': {
          log.log('Vibe stopped by user');
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              status: 'stopped',
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: '🛑 Generation stopped',
                timestamp: new Date().toISOString()
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        case 'vibe_generation_chunk': {
          // Streaming chunk during change requests - no state update needed
          // Could optionally append to a streaming buffer if we want to show live output
          break;
        }

        case 'vibe_blueprint': {
          // Blueprint analysis result from backend
          log.log('Vibe blueprint:', message.blueprint);
          setWorkflowState(prev => ({
            ...prev,
            deployment: {
              ...prev.deployment,
              blueprint: message.blueprint,
              chatHistory: [...(prev.deployment.chatHistory || []), {
                role: 'system',
                content: `📋 ${message.blueprint?.interpretation || 'Analyzing...'} (${message.blueprint?.complexity || 'unknown'})`,
                timestamp: message.timestamp
              }]
            },
            lastUpdatedAt: Date.now(),
          }));
          break;
        }

        default:
          // Suppress state-only sync messages (SDK internal noise)
          if (message.state || message.data?.state) {
            break;
          }
          log.log('Unhandled message:', type, JSON.stringify(message).substring(0, 300));
      }
    } catch (error) {
      log.error('Failed to parse message:', error);
    }
  }, []);

  /**
   * Handle connection state changes
   */
  const handleConnectionChange = useCallback((connected, agent) => {
    agentRef.current = agent;
    setWorkflowState(prev => ({ ...prev, wsConnected: connected }));
  }, []);

  /**
   * Start a new workflow and connect to agent
   * 🔄 UNIFIED: Now handles both 'queued' and 'started' status from API
   */
  const startWorkflow = useCallback(({ workflowId, agentId, sessionId, status, position, estimatedWaitMinutes }) => {
    log.log('Starting workflow:', { workflowId, agentId, status });

    // 🔄 UNIFIED: Handle both queued and started cases
    const isQueued = status === 'queued';

    setWorkflowState({
      ...initialWorkflowState,
      workflowId,
      agentId,
      sessionId,
      isActive: true,
      status: status || 'started',
      startedAt: Date.now(),
      lastUpdatedAt: Date.now(),
      // Queue fields (only populated if queued)
      queuePosition: isQueued ? position : null,
      queueEstimatedWait: isQueued ? estimatedWaitMinutes : null,
      queueMessage: isQueued ? `You're #${position} in queue` : null,
    });

    // 🔧 No client-side storage needed - backend tracks active workflows
    // 🔄 UNIFIED: Set agent ID to trigger WebSocket connection (works for both queued and started)
    // Agent's state will sync via WebSocket, updating queue position or workflow progress
    setCurrentAgentId(agentId);
  }, []);

  /**
   * Connect to an existing workflow
   */
  const connectWebSocket = useCallback((agentId) => {
    log.log('Connecting to agent:', agentId);
    setCurrentAgentId(agentId);
  }, []);

  /**
   * Disconnect from agent
   */
  const disconnectWebSocket = useCallback(() => {
    log.log('Disconnecting');
    setCurrentAgentId(null);
    setWorkflowState(prev => ({ ...prev, wsConnected: false }));
  }, []);

  /**
   * Clear workflow state
   */
  const clearWorkflow = useCallback(() => {
    log.log('Clearing workflow');
    setCurrentAgentId(null);
    setWorkflowState(initialWorkflowState);
    // 🔧 No client-side storage to clear - backend is source of truth
  }, []);

  /**
   * Cancel a running workflow via WebSocket
   * Sends cancel message to agent, which broadcasts workflow_cancelled back
   */
  const cancelWorkflow = useCallback(async (reason = 'User cancelled') => {
    log.log('Cancelling workflow via WebSocket');

    if (!agentRef.current) {
      log.error('No WebSocket connection to cancel');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'cancel_workflow',
        reason
      }));
      // State will be updated when we receive workflow_cancelled broadcast
      return { success: true };
    } catch (error) {
      log.error('Cancel send failed:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Update workflow state manually
   */
  const updateWorkflowState = useCallback((updates) => {
    setWorkflowState(prev => ({
      ...prev,
      ...updates,
      lastUpdatedAt: Date.now(),
    }));
  }, []);

  /**
   * Page tracking for GlobalWorkflowIndicator
   */
  const setWorkflowPageActive = useCallback((path) => {
    setActivePagePath(path);
  }, []);

  const clearWorkflowPageActive = useCallback((path) => {
    setActivePagePath(prev => prev === path ? null : prev);
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // 🚀 DEPLOYMENT METHODS
  // Same agent, same WebSocket - deployment actions sent here
  // ═══════════════════════════════════════════════════════════════

  /**
   * Start deployment for a session
   * Called after HTTP /api/v2/deploy/start returns agentId
   */
  const startDeployment = useCallback(({
    deploymentId,
    agentId,
    sessionId,
    status,
    phase,
    files,
    filesCount,
    previewUrl,
    deployedUrl,
    githubUrl,
  }) => {
    log.log('Starting deployment:', { deploymentId, agentId, sessionId, status, filesCount: filesCount || Object.keys(files || {}).length });

    // Update state to indicate deployment mode
    // If files are provided (resumed deployment), restore them
    setWorkflowState(prev => ({
      ...prev,
      agentId,
      sessionId,
      deployment: {
        ...prev.deployment,
        isActive: true,
        deploymentId,
        sessionId,  // Store sessionId in deployment for resume
        status: status || 'initialized',
        phase: phase || 'idle',
        generationPhase: phase,
        files: files || prev.deployment.files || {},
        filesCount: filesCount || Object.keys(files || {}).length || 0,
        previewUrl: previewUrl || prev.deployment.previewUrl,
        deployedUrl: deployedUrl || prev.deployment.deployedUrl,
        githubUrl: githubUrl || prev.deployment.githubUrl,
        error: null,
      },
      lastUpdatedAt: Date.now(),
    }));

    // Connect to agent WebSocket
    setCurrentAgentId(agentId);
  }, []);

  /**
   * Send start_deploy message via WebSocket
   * Called after WebSocket is connected
   */
  const sendStartDeploy = useCallback((payload = {}) => {
    log.log('Sending start_deploy');

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'start_deploy',
        ...payload,
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send start_deploy:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Send chat message during deployment
   */
  const sendDeployChat = useCallback((message) => {
    log.log('Sending deploy chat:', message.substring(0, 50));

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'deploy_chat',
        message,
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send deploy_chat:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Approve deployment to production
   */
  const approveDeploy = useCallback(() => {
    log.log('Approving deployment');

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'deploy_approve',
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send deploy_approve:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Pause deployment - stops generation but keeps files in DB for resume
   */
  const pauseDeploy = useCallback(() => {
    log.log('Pausing deployment');

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'deploy_pause',
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send deploy_pause:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Clear deployment - wipes everything from DB and memory
   */
  const clearDeploy = useCallback(() => {
    log.log('Clearing deployment (full reset)');

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      // Still clear local state even without connection
      setWorkflowState(prev => ({
        ...prev,
        deployment: initialWorkflowState.deployment,
      }));
      return { success: true };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'deploy_clear',
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send deploy_clear:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Resume deployment - continues from paused/failed state with existing files
   */
  const resumeDeploy = useCallback(() => {
    const deployment = workflowState.deployment;
    log.log('Resuming deployment:', { sessionId: deployment?.sessionId, deploymentId: deployment?.deploymentId });

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'start_deploy',
        resume: true,
        sessionId: deployment?.sessionId,
        deploymentId: deployment?.deploymentId,
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send resume deploy:', error);
      return { success: false, error: error.message };
    }
  }, [workflowState.deployment]);

  /**
   * Clear deployment state (local only - use clearDeploy for full reset)
   */
  const clearDeployment = useCallback(() => {
    log.log('Clearing deployment state (local only)');
    setWorkflowState(prev => ({
      ...prev,
      deployment: initialWorkflowState.deployment,
    }));
  }, []);

  /**
   * Cancel deployment (legacy - redirects to pause for backwards compatibility)
   */
  const cancelDeploy = useCallback(() => {
    log.log('Cancelling deployment (legacy - using pause)');
    return pauseDeploy();
  }, [pauseDeploy]);

  // ═══════════════════════════════════════════════════════════════
  // 🎨 VIBE CODING METHODS
  // Same agent, same WebSocket - vibe actions sent here
  // ═══════════════════════════════════════════════════════════════

  /**
   * Send start_vibe message to begin vibe coding session
   */
  const sendVibeStart = useCallback((idea, sessionId) => {
    log.log('Sending start_vibe:', idea?.substring(0, 50));

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      // Optimistic update: add user message to chat immediately
      setWorkflowState(prev => ({
        ...prev,
        deployment: {
          ...prev.deployment,
          status: 'generating',
          chatHistory: [...(prev.deployment?.chatHistory || []), {
            role: 'user',
            content: idea,
            timestamp: new Date().toISOString(),
          }],
        }
      }));

      agentRef.current.send(JSON.stringify({
        type: 'start_vibe',
        idea,
        sessionId,
        userId: user?.id,  // Required for deployment record creation
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send start_vibe:', error);
      return { success: false, error: error.message };
    }
  }, [user?.id]);

  /**
   * Send vibe_chat message during vibe coding session
   */
  const sendVibeChat = useCallback((message, sessionId) => {
    log.log('Sending vibe_chat:', message?.substring(0, 50));

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      // Optimistic update: add user message to chat immediately
      setWorkflowState(prev => ({
        ...prev,
        deployment: {
          ...prev.deployment,
          status: 'generating',
          chatHistory: [...(prev.deployment?.chatHistory || []), {
            role: 'user',
            content: message,
            timestamp: new Date().toISOString(),
          }],
        }
      }));

      agentRef.current.send(JSON.stringify({
        type: 'vibe_chat',
        message,
        sessionId,
        userId: user?.id,  // For consistency with backend
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send vibe_chat:', error);
      return { success: false, error: error.message };
    }
  }, [user?.id]);

  /**
   * Stop vibe coding session
   */
  const sendVibeStop = useCallback(() => {
    log.log('Sending stop_vibe');

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'stop_vibe',
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send stop_vibe:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Toggle preview visibility (private <-> public)
   * @param {boolean} makePublic - Whether to make the preview public
   * @param {string} sessionId - Session ID
   */
  const sendToggleVisibility = useCallback((makePublic, sessionId) => {
    log.log('Sending toggle_preview_visibility:', makePublic ? 'public' : 'private');

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'toggle_preview_visibility',
        makePublic,
        sessionId,
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send toggle_preview_visibility:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Push vibe files to user's GitHub repository
   * @param {string} repoName - Name for the new repository
   * @param {boolean} isPrivate - Whether repo should be private (default: true)
   */
  const sendVibePushToGitHub = useCallback((repoName, isPrivate = true) => {
    log.log('Sending vibe_push_github:', repoName);

    if (!agentRef.current) {
      log.error('No WebSocket connection');
      return { success: false, error: 'Not connected' };
    }

    try {
      agentRef.current.send(JSON.stringify({
        type: 'vibe_push_github',
        repoName,
        isPrivate,
        sessionId: workflowState.deployment?.sessionId,
        userId: user?.id,
      }));
      return { success: true };
    } catch (error) {
      log.error('Failed to send vibe_push_github:', error);
      return { success: false, error: error.message };
    }
  }, [user?.id, workflowState.deployment?.sessionId]);

  // ═══════════════════════════════════════════════════════════════
  // 🔄 UNIFIED QUEUE ARCHITECTURE
  // Queue state is managed via workflowState - agent WebSocket handles updates
  // No separate QueueManager WebSocket needed
  // ═══════════════════════════════════════════════════════════════

  /**
   * 🔧 UNIFIED: Check for active workflow on mount
   * Backend is source of truth - workflow can be 'queued', 'started', 'in_progress', etc.
   * Agent WebSocket handles all state sync (including queue updates)
   *
   * 🔧 FIX: Only runs when user is authenticated to prevent 401 errors for guests
   */
  useEffect(() => {
    const checkActiveWorkflow = async () => {
      // 🔧 FIX: Skip API call if not authenticated (prevents 401 for guests)
      if (authLoading || authType !== 'authenticated') {
        return;
      }

      // Skip if already connected or active
      if (currentAgentId || workflowState.isActive) {
        return;
      }

      const host = import.meta.env.DEV ? 'http://localhost:8787' : '';

      try {
        const workflowResponse = await fetch(`${host}/api/v2/session/active`, { credentials: 'include' });

        if (!workflowResponse.ok) {
          log.log('No auth session or request failed');
          return;
        }

        const data = await workflowResponse.json();

        if (!data.success || !data.workflow) {
          log.log('No active workflow found');
          return;
        }

        const { workflow } = data;
        log.log('Found workflow:', workflow.status, workflow.workflowId);

        // Handle completed workflow - ignore it, don't set state
        if (workflow.status === 'completed') {
          log.log('Workflow already completed - ignoring');
          return;
        }

        // Cancelled/failed/abandoned workflows are NOT active - don't reconnect
        // Frontend will load partial data from DB via loadSession
        if (['failed', 'cancelled', 'abandoned'].includes(workflow.status)) {
          log.log('Workflow ended with status:', workflow.status);
          return;
        }

        // 🔄 UNIFIED: Active workflow includes 'queued' status
        // Agent WebSocket will provide queue updates and state sync
        const isActiveStatus = ['queued', 'started', 'in_progress'].includes(workflow.status) ||
          ['research', 'validation', 'analysis', 'prompts', 'diagrams'].includes(workflow.currentStep);

        if (isActiveStatus) {
          // 🔒 SECURITY: Verify agentId belongs to current user before connecting
          // Format: architect_{userId}_{analysisId}
          const agentUserId = workflow.agentId?.replace('architect_', '').split('_').slice(0, -1).join('_');
          const currentUserId = user?.id;

          if (agentUserId && currentUserId && agentUserId !== currentUserId) {
            log.warn('Agent userId mismatch, skipping stale workflow', {
              agentUserId,
              currentUserId,
              agentId: workflow.agentId
            });
            return;
          }

          log.log('Active workflow found, reconnecting WebSocket:', workflow.agentId);

          const isQueued = workflow.status === 'queued';

          setWorkflowState({
            ...initialWorkflowState,
            workflowId: workflow.workflowId,
            agentId: workflow.agentId,
            sessionId: workflow.sessionId,
            isActive: true,
            status: workflow.status || 'in_progress',
            currentStep: STEP_MAP[workflow.currentStep] || (isQueued ? 1 : 2),
            stepName: workflow.currentStep,
            progress: workflow.progress || null,
            batchProgress: workflow.batchProgress || null,
            startedAt: Date.now(),
            lastUpdatedAt: Date.now(),
            // Queue fields from agent state
            queuePosition: workflow.queuePosition || null,
            queueEstimatedWait: workflow.queueEstimatedWait || null,
            queueMessage: workflow.queueMessage || null,
          });

          // Trigger WebSocket connection - agent state sync will update queue info
          setCurrentAgentId(workflow.agentId);
        }

      } catch (error) {
        log.error('Failed to check active workflow:', error);
        // Network error - don't block app, user can retry
      }
    };

    checkActiveWorkflow();
  }, [authLoading, authType, user?.session?.id]); // 🔧 FIX: Re-run when session changes (OAuth re-login)

  // Computed values
  const isWorkflowMode = workflowState.isActive &&
    !['completed', 'failed', 'cancelled'].includes(workflowState.status);

  const isComplete = workflowState.status === 'completed';
  const isFailed = workflowState.status === 'failed';
  const isCancelled = workflowState.status === 'cancelled';

  // 🔄 UNIFIED: Queue mode is now determined from workflowState.status
  const isQueueMode = workflowState.status === 'queued';
  const shouldHideGlobalIndicator = activePagePath !== null && (isWorkflowMode || isQueueMode);

  // Memoize context value
  const value = useMemo(() => ({
    workflowState,
    isWorkflowMode,
    isComplete,
    isFailed,
    isCancelled,
    shouldHideGlobalIndicator,
    activePagePath,
    agent: agentRef.current,

    updateWorkflowState,
    startWorkflow,
    clearWorkflow,
    cancelWorkflow,
    connectWebSocket,
    disconnectWebSocket,
    setWorkflowPageActive,
    clearWorkflowPageActive,

    // 🔄 UNIFIED: Queue mode is determined from workflowState.status
    isQueueMode,

    // 🚀 DEPLOYMENT: Methods for deployment flow
    startDeployment,
    sendStartDeploy,
    sendDeployChat,
    approveDeploy,
    cancelDeploy,
    pauseDeploy,
    clearDeploy,
    resumeDeploy,
    clearDeployment,

    // 🎨 VIBE CODING: Methods for vibe coding flow
    sendVibeStart,
    sendVibeChat,
    sendVibeStop,
    sendToggleVisibility,
    sendVibePushToGitHub,
  }), [
    workflowState,
    isWorkflowMode,
    isComplete,
    isFailed,
    isCancelled,
    shouldHideGlobalIndicator,
    activePagePath,
    updateWorkflowState,
    startWorkflow,
    clearWorkflow,
    cancelWorkflow,
    connectWebSocket,
    disconnectWebSocket,
    setWorkflowPageActive,
    clearWorkflowPageActive,
    isQueueMode,
    startDeployment,
    sendStartDeploy,
    sendDeployChat,
    approveDeploy,
    cancelDeploy,
    pauseDeploy,
    clearDeploy,
    resumeDeploy,
    clearDeployment,
    sendVibeStart,
    sendVibeChat,
    sendVibeStop,
    sendToggleVisibility,
    sendVibePushToGitHub,
  ]);

  return (
    <WorkflowContext.Provider value={value}>
      {/* Only render AgentConnection when we have a valid agent ID */}
      {currentAgentId && (
        <AgentConnection
          agentId={currentAgentId}
          onMessage={handleAgentMessage}
          onConnectionChange={handleConnectionChange}
        />
      )}
      {children}
    </WorkflowContext.Provider>
  );
}

/**
 * Hook to consume workflow context
 */
export function useWorkflow() {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
}

/**
 * Hook for read-only workflow status
 */
export function useWorkflowStatus() {
  const {
    workflowState,
    isWorkflowMode,
    isComplete,
    isQueueMode,
    shouldHideGlobalIndicator
  } = useWorkflow();

  // 🚀 DEPLOYMENT: Extract deployment state
  const deployment = workflowState.deployment;
  const isDeploymentMode = workflowState.isDeploymentActive || false;

  return {
    isActive: isWorkflowMode || isQueueMode || isDeploymentMode,  // 🔄 Active when queued, workflow, OR deployment
    isComplete,
    isQueueMode,  // 🔄 UNIFIED: True when status === 'queued'
    workflowId: workflowState.workflowId,
    status: workflowState.status,
    currentStep: workflowState.currentStep,
    stepName: workflowState.stepName,
    progress: workflowState.progress,
    loadingTitle: workflowState.loadingTitle,
    loadingMessage: workflowState.loadingMessage,
    batchProgress: workflowState.batchProgress,
    wsConnected: workflowState.wsConnected,
    error: workflowState.error,
    data: workflowState.data,
    shouldHideGlobalIndicator,
    startedAt: workflowState.startedAt,
    // 🔄 UNIFIED QUEUE: Queue fields from workflowState
    queuePosition: workflowState.queuePosition,
    queueEstimatedWait: workflowState.queueEstimatedWait,
    queueMessage: workflowState.queueMessage,
    // 🎯 RATE LIMIT PROTECTION: Capacity waiting state
    isWaitingForCapacity: workflowState.isWaitingForCapacity,
    waitingStep: workflowState.waitingStep,
    waitingMessage: workflowState.waitingMessage,
    waitingData: workflowState.waitingData,
    // 🚀 DEPLOYMENT: Deployment state fields
    isDeploymentMode,
    deployment,
    deploymentId: deployment?.deploymentId,
    deploymentStatus: deployment?.status,
    deploymentPhase: deployment?.phase,
    deploymentFilesCount: deployment?.filesCount,
    deploymentSessionId: deployment?.sessionId,
    deploymentPreviewUrl: deployment?.previewUrl,
    deploymentDeployedUrl: deployment?.deployedUrl,
    deploymentMode: deployment?.mode, // 'vibe' or 'deploy'
  };
}

export default WorkflowContext;
